'use client';

import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verify the HttpOnly session on load. A pre-migration bearer is exchanged
  // once and removed immediately; new browser logins never expose the JWT.
  useEffect(() => {
    const legacyToken = localStorage.getItem('authToken');
    (async () => {
      try {
        const response = await api.verifyToken(legacyToken);
        if (response.valid) {
          setUser(response.user);
          if (legacyToken) localStorage.removeItem('authToken');
        } else if (!response.networkFailure) {
          // The backend answered, so an invalid/expired legacy token is safe
          // to discard. Keep it only when migration failed due to connectivity.
          localStorage.removeItem('authToken');
        }
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (username, password) => {
    const response = await api.login(username, password);
    setUser(response.user);
    return response;
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
  }, []);

  // Stable context value: a fresh object every render would re-render every
  // consumer (header, pages) whenever the provider re-renders
  const value = useMemo(
    () => ({ user, login, logout, loading }),
    [user, loading, login, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
